import { OrderType } from '@/compiled_proto/com/celertech/positionmanager/api/enums/OrderTypeProto';
import { createLumeFxRestClient } from '@/services/LumeFxService';
import { addMarketFull } from '@/services/MarketService';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials } from '@/state/reducers/authSlice';
import { OrderPrefillValues } from '@/state/reducers/orderSlice';
import { selectSettings } from '@/state/reducers/settingSlice';
import { LumeFxOrderType, LumeFxSide, LumeFxTimeInForce } from '@/types/lumeFx';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import useLanguage from '@/utils/hooks/useLanguage';
import { invalidStopPriceMessage } from '@/utils/hooks/useOrderBook';
import { UseOrderExecutionReturn } from '@/utils/hooks/useOrderExecution';
import useTotals from '@/utils/hooks/useTotals';
import useTradeFeePreview from '@/utils/hooks/useTradeFeePreview';
import { useLucera } from '@/utils/providers/LuceraProvider';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDebouncedValue } from '@mantine/hooks';
import BigNumber from 'bignumber.js';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { MdInfo } from 'react-icons/md';
import FeePreview from '../common/FeePreview';
import Tooltip from '../common/Tooltip';
import ExecutionInputController from '../inputs/ExecutionInputController';
import NumberInputDisplay from '../inputs/NumberInputDisplay';
import RHFNumberInput from '../inputs/RHFNumberInput';
import Select from '../inputs/Select';
import SlippageInput from '../inputs/SlippageInput';
import { BalancePreview } from './components/BalancePreview';
import FormBody from './components/FormBody';
import FormCurrency from './components/FormCurrency';
import FormFooter from './components/FormFooter';
import FormOrderTypeSwitch from './components/FormOrderTypeSwitch';
import FormSubmit from './components/FormSubmit';
import Totals from './components/Totals';
import { StopOrderFormInput, StopOrderFormValues, stopOrderSchema } from './schema/stopOrderSchema';

const stopOrderInfo =
    'To limit risk of stops triggering on a temporary spread widening, Stops to Buy are triggered on the Bid, Stops to Sell are triggered on the Offer.';

const stopTypeOptions: { label: string; value: LumeFxOrderType.Stop | LumeFxOrderType.StopLimit }[] = [
    { label: 'STOP MARKET', value: LumeFxOrderType.Stop },
    { label: 'STOP LIMIT', value: LumeFxOrderType.StopLimit }
];

const durationOptions: { label: keyof typeof LumeFxTimeInForce; value: LumeFxTimeInForce }[] = [
    { label: 'DAY', value: LumeFxTimeInForce.DAY },
    { label: 'GTC', value: LumeFxTimeInForce.GTC }
];

const defaultValues: Partial<StopOrderFormInput> = {
    type: stopTypeOptions[0],
    stopPrice: null,
    quantity: null,
    duration: durationOptions[0],
    slippage: '0.00000'
};

interface StopOrderFormProps extends UseOrderExecutionReturn {
    parentSize?: number;
    prefillValues?: OrderPrefillValues;
    onClose?: () => void;
    closeOnSubmit?: boolean;
}

const luceraRestURL = window.config?.lucera?.rest;

function StopOrderForm(props: StopOrderFormProps) {
    const { parentSize = 0, prefillValues, onClose, closeOnSubmit = false, ...orderExecutionProps } = props;
    const {
        mode,
        ccy1,
        ccy2,
        sideState,
        currencyState,
        priceCurrency,
        activeCurrencyConfig,
        orderBook,
        instrument,
        pairMapToUse,
        minOrderSize,
        orderDecimals,
        ccy1Config,
        isCcy2Order
    } = orderExecutionProps;

    const lang = useLanguage();
    const settings = useAppSelector(selectSettings);
    const credentials = useAppSelector(selectCredentials);

    const { jwt } = useLucera();

    const lumeFxRestClient = createLumeFxRestClient({
        baseUrl: luceraRestURL,
        authorization: jwt
    });

    const [side, setSide] = sideState;
    const [currency, setCurrency] = currencyState;

    const { formatPrice, pip_size, defaultSlippage, price_decimals } = instrument;
    const { asks, bids, bestAsk, bestBid, bestPrice } = orderBook;

    const form = useForm<StopOrderFormInput>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(
            stopOrderSchema({
                priceOptions: {
                    side,
                    bestAsk,
                    bestBid,
                    formattedBestAsk: formatPrice(bestAsk),
                    formattedBestBid: formatPrice(bestBid),
                    ccy2Order: isCcy2Order
                },
                quantityOptions: {
                    min_order_size: minOrderSize,
                    order_decimals: orderDecimals,
                    currencyOut: currency,
                    currencyPair: pairMapToUse?.show
                }
            })
        )
    });

    const totals = useTotals({
        form,
        pairMap: pairMapToUse,
        side,
        ccy1: instrument?.type === 'Future' ? instrument?.config?.ccy2 || '' : ccy1,
        ccy2: instrument?.type === 'Future' ? instrument?.config?.ccy2 || '' : ccy2,
        ccy2Order: isCcy2Order,
        bids,
        asks
    });

    const {
        watch,
        reset,
        register,
        setValue,
        setError,
        clearErrors,
        handleSubmit,
        formState: { isSubmitting, isValid }
    } = form;

    const [quantity, stopType, stopPrice, slippage] = watch(['quantity', 'type.value', 'stopPrice', 'slippage']);
    const [debouncedQuantity] = useDebouncedValue(quantity, 300);

    const stopLimitPrice = useMemo(() => {
        const slip = BigNumber(+(slippage || 0)).dividedBy(Math.pow(10, pip_size || 0));
        const stop = +(stopPrice || 0);
        if (side === LumeFxSide.BUY) return BigNumber(stop).plus(slip).toNumber();
        if (side === LumeFxSide.SELL) return BigNumber(stop).minus(slip).toNumber();
        return stop;
    }, [stopPrice, slippage]);

    const { spotFeesApi, cfdFeesApi } = useTradeFeePreview({
        ...orderExecutionProps,
        side,
        isCfd: instrument.type === 'FX' ? false : true,
        debouncedQuantity
    });

    const { data: spotFees } = spotFeesApi;
    const { data: cfdFees } = cfdFeesApi;

    const fees = useMemo(() => spotFees || cfdFees, [spotFees, cfdFees]);

    const onSubmit = async (data: StopOrderFormInput, e) => {
        const dataValidated = data as StopOrderFormValues;
        if (dataValidated.quantity && credentials) {
            if (closeOnSubmit) onClose?.();
            if (dataValidated.type.value === LumeFxOrderType.Stop) {
                await lumeFxRestClient.placeOrder({
                    Account: '123',
                    Symbol: pairMapToUse?.celer,
                    TimeInForce: LumeFxTimeInForce.DAY,
                    OrdType: LumeFxOrderType.Stop,
                    Price: stopLimitPrice.toString(),
                    StopPx: stopPrice?.toString() || '0',
                    OrderQty: (dataValidated.quantity || 0).toString(),
                    ClOrdID: crypto.randomUUID(),
                    Side: side
                });
            } else {
                lumeFxRestClient.placeOrder({
                    Account: '123',
                    Symbol: pairMapToUse?.celer,
                    TimeInForce: LumeFxTimeInForce.DAY,
                    OrdType: LumeFxOrderType.StopLimit,
                    StopPx: stopPrice?.toString() || '0',
                    Price: stopLimitPrice.toString(),
                    OrderQty: (dataValidated.quantity || 0).toString(),
                    ClOrdID: crypto.randomUUID(),
                    Side: side
                });
            }
        }
    };

    const onError = (errors) => console.error(errors);

    const resetDefault = useCallback(
        (quantity?: number) => {
            // resets all fields, set limit price to default worst price
            const resetValues: typeof defaultValues = {
                ...defaultValues,
                stopPrice: getStopPrice(bestBid, bestAsk, side, isCcy2Order, price_decimals),
                slippage: defaultSlippage
            };

            if (quantity) resetValues.quantity = quantity;

            if (
                [LumeFxTimeInForce.DAY, LumeFxTimeInForce.GTC].includes(settings.trading.defaultStopOrderDuration.value)
            ) {
                resetValues.duration = settings.trading.defaultStopOrderDuration;
            }

            reset(resetValues);
        },
        [defaultValues, defaultSlippage, side, isCcy2Order, price_decimals, bestAsk, bestBid, settings]
    );

    useDidUpdate(() => {
        if (credentials) {
            if (debouncedQuantity && debouncedQuantity > 0) {
                // console.log('addMarketFull due to quantity', market.celer);
                addMarketFull(pairMapToUse.celer);
            }
        }
    }, [debouncedQuantity]);

    useDidUpdate(() => resetDefault(), [currency]);
    useDidUpdate(() => {
        if (prefillValues) {
            const sideToSet = (prefillValues.netQtyPosition || 0) < 0 ? LumeFxSide.SELL : LumeFxSide.BUY;
            if (side !== sideToSet) setSide(sideToSet); // This will trigger a reset default already
            else {
                const quantity = +Math.abs(prefillValues.netQtyPosition).toFixed(ccy1Config.order_decimals);
                resetDefault(quantity);
            }
        }
    }, [prefillValues]);

    useEffect(() => {
        const isCcy1Buy = side === LumeFxSide.BUY && !isCcy2Order;
        const isCcy2Buy = side === LumeFxSide.BUY && isCcy2Order;
        const isCcy1Sell = side === LumeFxSide.SELL && !isCcy2Order;
        const isCcy2Sell = side === LumeFxSide.SELL && isCcy2Order;

        if (stopPrice && bestBid && bestAsk) {
            if ((isCcy1Buy || isCcy2Sell) && +stopPrice <= bestBid) {
                const message = invalidStopPriceMessage(side, formatPrice(bestBid), isCcy2Order);
                setError('stopPrice', { type: 'stopPriceConstraint', message });
            } else if ((isCcy2Buy || isCcy1Sell) && +stopPrice >= bestAsk) {
                const message = invalidStopPriceMessage(side, formatPrice(bestAsk), isCcy2Order);
                setError('stopPrice', { type: 'stopPriceConstraint', message });
            } else {
                clearErrors('stopPrice');
            }
        }
    }, [bestBid, bestAsk, isCcy2Order, side]);

    useEffect(() => {
        if (prefillValues) {
            const quantity = +Math.abs(prefillValues.netQtyPosition).toFixed(ccy1Config.order_decimals);
            setValue('quantity', quantity, { shouldValidate: true });
        }
        setValue('stopPrice', getStopPrice(bestBid, bestAsk, side, isCcy2Order, price_decimals), {
            shouldValidate: true
        });
    }, [side]);

    useEffect(() => {
        if (prefillValues) {
            const sideToSet = (prefillValues.netQtyPosition || 0) < 0 ? LumeFxSide.SELL : LumeFxSide.BUY;
            if (side !== sideToSet) setSide(sideToSet); // This will trigger a reset default already
            else {
                const quantity = +Math.abs(prefillValues.netQtyPosition).toFixed(ccy1Config.order_decimals);
                resetDefault(quantity);
            }
        } else resetDefault();
    }, [pairMapToUse.celer]);

    return (
        <FormProvider {...form}>
            <form
                onSubmit={handleSubmit(onSubmit, onError)}
                className="relative flex flex-col h-full w-full text-brand-text">
                <FormBody side={side} mode={mode === 'popup' ? 'Modal' : 'Module'}>
                    <FormOrderTypeSwitch initialValue={side} onChange={setSide} />
                    <FormCurrency {...props} currency={currency} setCurrency={setCurrency} />
                    <div className="w-full relative">
                        <Tooltip content={stopOrderInfo} offset={6} className="max-w-sm">
                            <div className="absolute flex w-full justify-end right-0 top-0">
                                <MdInfo className="w-5 h-5" />
                            </div>
                        </Tooltip>
                        <ExecutionInputController name="type" label={`${lang.orderForm.stopType} *`} hasTooltipIcon>
                            <Select type="execution" options={stopTypeOptions} />
                        </ExecutionInputController>
                    </div>
                    <RHFNumberInput
                        label={`${lang.orderForm.stopPrice} *`}
                        placeholder={lang.orderForm.stopPrice}
                        {...register('stopPrice')}
                        step={BigNumber(1)
                            .dividedBy(Math.pow(10, pip_size || 0))
                            .toNumber()}
                        currency={priceCurrency.show}
                    />
                    <RHFNumberInput
                        {...register('quantity')}
                        label={`${lang.orderForm.quantity} *`}
                        placeholder={lang.orderForm.quantity}
                        min={minOrderSize}
                        step={activeCurrencyConfig.increments}
                        currency={activeCurrencyConfig.show}
                    />
                    <ExecutionInputController name="duration" label={`${lang.orderForm.duration} *`}>
                        <Select type="execution" options={durationOptions} />
                    </ExecutionInputController>
                    {stopType === OrderType.STOP_LIMIT && (
                        <SlippageInput
                            {...register('slippage')}
                            label={lang.orderForm.slippage}
                            placeholder={lang.orderForm.slippage}
                            defaultSlippage={defaultSlippage}
                            step={0.1}
                        />
                    )}

                    {stopType === OrderType.STOP_LIMIT && (
                        <Tooltip
                            content="The Stop Limit price is placed as the Stop Price +/- Slippage"
                            className="max-w-fit">
                            <ExecutionInputController
                                name="stopLimitPrice"
                                label={`${lang.orderForm.stopLimitPrice} *`}
                                value={stopLimitPrice}>
                                <NumberInputDisplay placeholder={lang.orderForm.stopLimitPrice} disabled />
                            </ExecutionInputController>
                        </Tooltip>
                    )}
                    <Totals {...totals} />
                    {instrument.type === 'FX' && quantity && (
                        <FeePreview
                            feesDetails={spotFees}
                            isLoadingFeesDetails={spotFeesApi.isLoading || spotFeesApi.isValidating}
                            feesDetailsError={spotFeesApi.error}
                        />
                    )}
                    {instrument.type !== 'FX' && quantity && (
                        <FeePreview
                            feesDetails={cfdFees}
                            isLoadingFeesDetails={cfdFeesApi.isLoading || cfdFeesApi.isValidating}
                            feesDetailsError={cfdFeesApi.error}
                        />
                    )}
                    {totals.total !== '-' && debouncedQuantity && (
                        <BalancePreview
                            fees={fees}
                            totals={totals}
                            quantity={debouncedQuantity}
                            parentSize={parentSize}
                            {...orderExecutionProps}
                        />
                    )}
                </FormBody>
                <FormFooter mode={mode === 'popup' ? 'Modal' : 'Module'}>
                    <FormSubmit
                        side={side}
                        isValid={isValid}
                        disabled={isSubmitting || !isValid || totals.disableSubmit || !instrument.type}
                        noInstrumentConfig={!instrument.type}
                        noSettlementDate={!orderBook.settlementDate}
                        className={mode === 'popup' ? 'left-6' : ''}
                    />
                </FormFooter>
            </form>
        </FormProvider>
    );
}

export default StopOrderForm;

export const convertToStopPrice = (price: number, side: LumeFxSide, isCcy2Order: boolean, priceDecimals?: number) => {
    const isCcy1Buy = side === LumeFxSide.BUY && !isCcy2Order;
    const isCcy2Buy = side === LumeFxSide.BUY && isCcy2Order;
    const isCcy1Sell = side === LumeFxSide.SELL && !isCcy2Order;
    const isCcy2Sell = side === LumeFxSide.SELL && isCcy2Order;

    if (isCcy1Buy || isCcy2Sell)
        return BigNumber(price)
            .multipliedBy(1.001)
            .decimalPlaces(priceDecimals || 0)
            .toNumber();
    else if (isCcy2Buy || isCcy1Sell)
        return BigNumber(price)
            .multipliedBy(0.999)
            .decimalPlaces(priceDecimals || 0)
            .toNumber();
    else return price;
};

export const getStopPrice = (
    bestBid: number,
    bestAsk: number,
    side: LumeFxSide,
    isCcy2Order: boolean,
    priceDecimals?: number
) => {
    const isCcy1Buy = side === LumeFxSide.BUY && !isCcy2Order;
    const isCcy2Buy = side === LumeFxSide.BUY && isCcy2Order;
    const isCcy1Sell = side === LumeFxSide.SELL && !isCcy2Order;
    const isCcy2Sell = side === LumeFxSide.SELL && isCcy2Order;

    if (bestBid && bestAsk) {
        if (isCcy1Buy || isCcy2Sell) return convertToStopPrice(bestBid, side, isCcy2Order, priceDecimals);
        else if (isCcy2Buy || isCcy1Sell) return convertToStopPrice(bestAsk, side, isCcy2Order, priceDecimals);
    }

    return 0;
};
